// src/components/TrendingFromNewsView.js
// ============================================================================
// Trending Vulnerabilities (driven by Cyber News mentions)
//
// Layout:
//   1. Header strip with stats + time window selector (7d / 14d / 30d)
//   2. Top trending CVE cards (grid) - the most-mentioned CVEs in news
//   3. Latest mentions table - newest CVE-tagged articles
//
// Click a trending tile (or a CVE id in the latest mentions table) to open a
// detail modal that mirrors the searched-CVE detail card from
// VulnerabilityIntelligenceView. Details are lazy-fetched from
// /api/vulnerabilities?search={cve_id} when the CVE exists in the local DB.
//
// Backend: GET /api/vulnerabilities/trending-from-news?days=7&limit=12
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  Flame, RefreshCw, AlertTriangle, ExternalLink, Newspaper,
  TrendingUp, Calendar, Shield, Eye, Globe2, ArrowUpRight,
  Github, Star, Target, X, Code, Bug
} from 'lucide-react';

const TrendingFromNewsView = ({ onTabChange, isSuperAdmin }) => {
  const { getToken } = useAuth();

  const [windowDays, setWindowDays] = useState(7);
  const [trending, setTrending] = useState([]);
  const [latestMentions, setLatestMentions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [githubEnriching, setGithubEnriching] = useState(false);

  // Detail modal state
  const [selectedCVE, setSelectedCVE] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const handleGithubEnrich = async () => {
    try {
      setGithubEnriching(true);
      const token = await getToken();
      const res = await fetch('/api/vulnerabilities/github/enrich-batch', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 30 }),
      });
      const data = await res.json();
      console.log('[GitHub] Batch enrich started:', data.message);
      setTimeout(() => fetchData(false), 8000);
    } catch (err) {
      console.error('[GitHub] Batch enrich error:', err);
    } finally {
      setGithubEnriching(false);
    }
  };

  // ── Data fetch ───────────────────────────────────────────────────────────
  const fetchData = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      else setRefreshing(true);
      const token = await getToken();

      const res = await fetch(
        `/api/vulnerabilities/trending-from-news?days=${windowDays}&limit=12`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to load trending CVEs from news');

      const data = await res.json();
      setTrending(data.trending || []);
      setLatestMentions(data.latest_mentions || []);
      setStats(data.stats || null);
      setError(null);
    } catch (err) {
      console.error('Trending from news error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getToken, windowDays]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // ── Open detail modal: seed with tile data, then lazy-load full record ──
  const openDetailFor = useCallback(async (cveLike) => {
    // Seed immediately so the modal renders with whatever we already have
    setSelectedCVE(cveLike);
    setDetailError(null);

    // Only attempt full hydration if we have a cve_id
    if (!cveLike?.cve_id) return;

    try {
      setDetailLoading(true);
      const token = await getToken();
      const res = await fetch(
        `/api/vulnerabilities?search=${encodeURIComponent(cveLike.cve_id)}&limit=5`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
      const data = await res.json();
      const exact = (data.vulnerabilities || []).find(
        v => (v.cve_id || '').toUpperCase() === cveLike.cve_id.toUpperCase()
      );
      if (exact) {
        // Merge: keep tile-only fields (mention_count, top_articles, etc.) and
        // overlay the full vuln record on top
        setSelectedCVE(prev => ({ ...prev, ...exact }));
      }
    } catch (err) {
      console.error('CVE detail lookup error:', err);
      setDetailError(err.message);
    } finally {
      setDetailLoading(false);
    }
  }, [getToken]);

  const closeDetail = () => {
    setSelectedCVE(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  // Close on Escape
  useEffect(() => {
    if (!selectedCVE) return;
    const onKey = (e) => { if (e.key === 'Escape') closeDetail(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedCVE]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getSeverityStyle = (sev) => {
    switch (sev) {
      case 'Critical': return 'bg-red-500/20 text-red-300 border-red-500/40';
      case 'High':     return 'bg-orange-500/20 text-orange-300 border-orange-500/40';
      case 'Medium':   return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40';
      case 'Low':      return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
      default:         return 'bg-slate-700/40 text-slate-400 border-slate-600/40';
    }
  };

  const getCvssStyle = (score) => {
    if (!score) return 'text-slate-500';
    if (score >= 9.0) return 'text-red-400';
    if (score >= 7.0) return 'text-orange-400';
    if (score >= 4.0) return 'text-yellow-400';
    return 'text-blue-400';
  };

  const getHeatTier = (count, max) => {
    if (!max) return { color: 'bg-slate-700/40 text-slate-400 border-slate-600/40', label: 'Cool' };
    const pct = count / max;
    if (pct >= 0.7) return { color: 'bg-red-500/20 text-red-300 border-red-500/40',     label: '🔥 Blazing' };
    if (pct >= 0.4) return { color: 'bg-orange-500/20 text-orange-300 border-orange-500/40', label: '🔥 Hot' };
    if (pct >= 0.2) return { color: 'bg-amber-500/20 text-amber-300 border-amber-500/40',    label: 'Warm' };
    return { color: 'bg-slate-700/40 text-slate-300 border-slate-600/40', label: 'Rising' };
  };

  const maxMentions = trending.reduce((m, t) => Math.max(m, t.mention_count || 0), 0);

  const formatRelativeTime = (iso) => {
    if (!iso) return '—';
    const diffMs = Date.now() - new Date(iso).getTime();
    const hrs = Math.floor(diffMs / (1000 * 60 * 60));
    if (hrs < 1) return 'just now';
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 text-orange-400 animate-spin" />
          <p className="text-slate-400">Analyzing news feeds for trending CVEs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-950">
      {/* HEADER STRIP */}
      <div className="sticky top-0 z-20 bg-gradient-to-b from-slate-950 via-slate-950/95 to-slate-950/80 backdrop-blur-sm border-b border-slate-800/50 px-6 py-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
              <Flame className="w-6 h-6 text-orange-400" />
              Trending Vulnerabilities
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              CVEs driving the conversation across {stats?.sources_reporting ?? 0} cyber news sources in the last {windowDays} days.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Tab switcher */}
            {onTabChange && (
              <div className="flex bg-slate-800/50 border border-slate-700/50 rounded-lg p-1">
                <button
                  onClick={() => onTabChange('database')}
                  className="px-3 py-1.5 text-xs font-medium rounded text-slate-400 hover:text-slate-200 transition-all flex items-center gap-1"
                >
                  <Shield className="w-3 h-3" /> CVEs
                </button>
                <button className="px-3 py-1.5 text-xs font-medium rounded bg-orange-500/20 text-orange-300 border border-orange-500/30 flex items-center gap-1">
                  <Flame className="w-3 h-3" /> Trending
                </button>
                <button
                  onClick={() => onTabChange('recommended')}
                  className="px-3 py-1.5 text-xs font-medium rounded text-slate-400 hover:text-slate-200 transition-all flex items-center gap-1"
                >
                  <Target className="w-3 h-3" /> For You
                </button>
              </div>
            )}

            {/* Window selector */}
            <div className="flex bg-slate-800/50 border border-slate-700/50 rounded-lg p-1">
              {[7, 14, 30].map(d => (
                <button
                  key={d}
                  onClick={() => setWindowDays(d)}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                    windowDays === d
                      ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>

            {/* Refresh */}
            <button
              onClick={() => fetchData(false)}
              disabled={refreshing}
              className="px-3 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-300 hover:text-slate-100 hover:border-slate-600/50 transition-all flex items-center gap-1.5 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {/* GitHub enrich (Super Admin) */}
            {isSuperAdmin && (
              <button
                onClick={handleGithubEnrich}
                disabled={githubEnriching}
                className="px-3 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-300 hover:text-slate-100 hover:border-slate-600/50 transition-all flex items-center gap-1.5 disabled:opacity-50"
              >
                <Github className={`w-3 h-3 ${githubEnriching ? 'text-green-400 animate-pulse' : 'text-slate-400'}`} />
                {githubEnriching ? 'Enriching...' : 'GitHub'}
              </button>
            )}
          </div>
        </div>

        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <StatCard
              icon={<Flame className="w-4 h-4 text-orange-400" />}
              label="Distinct CVEs"
              value={stats.distinct_cves}
            />
            <StatCard
              icon={<Newspaper className="w-4 h-4 text-cyan-400" />}
              label="Articles w/ CVEs"
              value={stats.articles_with_cves}
            />
            <StatCard
              icon={<Globe2 className="w-4 h-4 text-purple-400" />}
              label="Sources Reporting"
              value={stats.sources_reporting}
            />
            <StatCard
              icon={<TrendingUp className="w-4 h-4 text-green-400" />}
              label="Top Mentions"
              value={maxMentions}
            />
          </div>
        )}
      </div>

      {error && (
        <div className="m-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-300">Failed to load trending data</p>
            <p className="text-xs text-red-400/80 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* TRENDING CARDS */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2">
            <Flame className="w-4 h-4 text-orange-400" />
            Most Talked About
          </h3>
          <span className="text-xs text-slate-500">{trending.length} CVEs</span>
        </div>

        {trending.length === 0 && !error && (
          <div className="p-12 text-center bg-slate-900/30 border border-slate-800/50 rounded-xl">
            <Newspaper className="w-12 h-12 mx-auto mb-3 text-slate-700" />
            <p className="text-slate-400 font-medium">No CVE mentions in the news yet</p>
            <p className="text-sm text-slate-500 mt-1">Try widening the time window or check back after the next news sync.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {trending.map((cve) => {
            const heat = getHeatTier(cve.mention_count, maxMentions);
            const isExpanded = expandedCard === cve.cve_id;

            return (
              <div
                key={cve.cve_id}
                onClick={() => openDetailFor(cve)}
                className="bg-gradient-to-br from-slate-900/80 to-slate-900/40 border border-slate-800/60 rounded-xl p-4 hover:border-orange-500/40 hover:shadow-lg hover:shadow-orange-500/5 transition-all group flex flex-col cursor-pointer"
              >
                {/* Card header: CVE id + heat badge + GitHub count */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className="font-mono font-bold text-cyan-400 text-base group-hover:text-cyan-300 transition-colors">
                    {cve.cve_id}
                  </span>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {cve.github_repo_count != null && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border flex items-center gap-1 ${
                        cve.github_repo_count > 50 ? 'bg-green-500/20 border-green-500/30 text-green-300'
                        : cve.github_repo_count > 10 ? 'bg-blue-500/20 border-blue-500/30 text-blue-300'
                        : 'bg-slate-700/40 border-slate-600/40 text-slate-400'
                      }`}>
                        <Github className="w-2.5 h-2.5" />
                        {cve.github_repo_count > 999 ? '999+' : cve.github_repo_count}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-md text-xs font-medium border whitespace-nowrap ${heat.color}`}>
                      {heat.label}
                    </span>
                  </div>
                </div>

                {/* Mention count - the headline metric */}
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-3xl font-bold text-orange-400">{cve.mention_count}</span>
                  <span className="text-xs text-slate-400">
                    mention{cve.mention_count === 1 ? '' : 's'} · {cve.source_count} source{cve.source_count === 1 ? '' : 's'}
                  </span>
                </div>

                {/* Severity + CVSS row (only if we have local vuln data) */}
                {(cve.severity || cve.cvss_score || cve.epss_score != null) && (
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {cve.severity && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getSeverityStyle(cve.severity)}`}>
                        {cve.severity}
                      </span>
                    )}
                    {cve.cvss_score && (
                      <span className={`text-xs font-bold ${getCvssStyle(cve.cvss_score)}`}>
                        CVSS {cve.cvss_score}
                      </span>
                    )}
                    {cve.cisa_kev && (
                      <span className="px-1.5 py-0.5 bg-red-500/20 border border-red-500/40 rounded text-[10px] text-red-300 font-medium">
                        KEV
                      </span>
                    )}
                    {cve.epss_score != null && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                        cve.epss_percentile >= 0.9 ? 'bg-red-500/20 border-red-500/40 text-red-300'
                        : cve.epss_percentile >= 0.7 ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                        : 'bg-slate-700/40 border-slate-600/40 text-slate-400'
                      }`}>
                        EPSS {(cve.epss_score * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                )}

                {/* Optional title from local vuln data */}
                {cve.vuln_title && (
                  <p className="text-xs text-slate-400 line-clamp-2 mb-3">
                    {cve.vuln_title}
                  </p>
                )}

                {/* Top article preview — stopPropagation so the link doesn't open the modal */}
                {cve.top_articles && cve.top_articles[0] && (
                  <a
                    href={cve.top_articles[0].article_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block bg-slate-950/50 border border-slate-800/60 rounded-lg p-2.5 mb-3 hover:border-cyan-500/30 hover:bg-slate-950/80 transition-all"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs text-slate-300 line-clamp-2 flex-1 group-hover:text-slate-200">
                        {cve.top_articles[0].article_title}
                      </p>
                      <ExternalLink className="w-3 h-3 text-slate-500 flex-shrink-0 mt-0.5" />
                    </div>
                    <div className="flex items-center justify-between mt-1.5 text-[10px] text-slate-500">
                      <span className="truncate">{cve.top_articles[0].source_name}</span>
                      <span className="flex-shrink-0 ml-2">{formatRelativeTime(cve.top_articles[0].published_at)}</span>
                    </div>
                  </a>
                )}

                {/* Expand for more articles — stopPropagation so the toggle doesn't open the modal */}
                {cve.top_articles && cve.top_articles.length > 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setExpandedCard(isExpanded ? null : cve.cve_id); }}
                    className="text-xs text-slate-400 hover:text-cyan-300 transition-colors mt-auto text-left"
                  >
                    {isExpanded ? '− Hide' : '+ Show'} {cve.top_articles.length - 1} more article{cve.top_articles.length - 1 === 1 ? '' : 's'}
                  </button>
                )}

                {isExpanded && cve.top_articles?.slice(1).map((art, i) => (
                  <a
                    key={i}
                    href={art.article_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="block mt-2 p-2 rounded text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 transition-all border-l-2 border-slate-700"
                  >
                    <p className="line-clamp-2">{art.article_title}</p>
                    <span className="text-[10px] text-slate-500 mt-1 block">
                      {art.source_name} · {formatRelativeTime(art.published_at)}
                    </span>
                  </a>
                ))}

                {/* GitHub repos — shown when expanded */}
                {isExpanded && cve.github_top_repos && cve.github_top_repos.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-700/50">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Github className="w-3 h-3" />
                      GitHub Repositories ({cve.github_repo_count})
                    </p>
                    {cve.github_top_repos.map((repo, i) => (
                      <a
                        key={i}
                        href={repo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-between mt-1.5 p-2 rounded bg-slate-800/50 hover:bg-slate-800 transition-all group/repo"
                      >
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-mono text-cyan-400 group-hover/repo:text-cyan-300 truncate block">{repo.name}</span>
                          {repo.description && <span className="text-[10px] text-slate-500 line-clamp-1">{repo.description}</span>}
                        </div>
                        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                          {repo.language && <span className="text-[10px] text-slate-500 bg-slate-700/50 px-1.5 py-0.5 rounded">{repo.language}</span>}
                          <Star className="w-3 h-3 text-slate-500" />
                          <span className="text-[10px] text-slate-500">{repo.stars?.toLocaleString() || 0}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* LATEST MENTIONS TABLE */}
      <div className="px-6 pb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2">
            <Newspaper className="w-4 h-4 text-cyan-400" />
            Latest CVE Mentions in News
          </h3>
          <span className="text-xs text-slate-500">{latestMentions.length} articles</span>
        </div>

        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/60 border-b border-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">CVE</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Article</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Published</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-400 uppercase tracking-wider">Link</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40">
                {latestMentions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      No CVE mentions in the selected window.
                    </td>
                  </tr>
                )}
                {latestMentions.map((m, i) => (
                  <tr key={`${m.news_id}-${m.cve_id}-${i}`} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => openDetailFor({
                          cve_id: m.cve_id,
                          severity: m.severity,
                          cvss_score: m.cvss_score,
                          exploit_available: m.exploit_available,
                          in_the_wild: m.in_the_wild,
                          cisa_kev: m.cisa_kev,
                        })}
                        className="font-mono text-sm font-semibold text-cyan-400 hover:text-cyan-300 hover:underline"
                      >
                        {m.cve_id}
                      </button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {m.severity ? (
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getSeverityStyle(m.severity)}`}>
                            {m.severity}
                          </span>
                          {m.cvss_score && (
                            <span className={`text-xs font-bold ${getCvssStyle(m.cvss_score)}`}>
                              {m.cvss_score}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-600 italic">not in DB</span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <p className="text-sm text-slate-300 line-clamp-2">{m.article_title}</p>
                      {(m.exploit_available || m.in_the_wild || m.cisa_kev) && (
                        <div className="flex items-center gap-1.5 mt-1">
                          {m.exploit_available && (
                            <span className="px-1.5 py-0.5 bg-red-500/15 border border-red-500/30 rounded text-[10px] text-red-300">Exploit</span>
                          )}
                          {m.in_the_wild && (
                            <span className="px-1.5 py-0.5 bg-orange-500/15 border border-orange-500/30 rounded text-[10px] text-orange-300">In Wild</span>
                          )}
                          {m.cisa_kev && (
                            <span className="px-1.5 py-0.5 bg-red-500/15 border border-red-500/30 rounded text-[10px] text-red-300">KEV</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-400">{m.source_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500">
                      {formatRelativeTime(m.published_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <a
                        href={m.article_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                      >
                        Read <ArrowUpRight className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* DETAIL MODAL */}
      {selectedCVE && (
        <CVEDetailModal
          cve={selectedCVE}
          loading={detailLoading}
          error={detailError}
          onClose={closeDetail}
          getSeverityStyle={getSeverityStyle}
          getCvssStyle={getCvssStyle}
          formatRelativeTime={formatRelativeTime}
        />
      )}
    </div>
  );
};

// ── Small stat card ────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value }) => (
  <div className="bg-slate-900/40 border border-slate-800/50 rounded-lg px-3 py-2.5">
    <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
      {icon}
      <span>{label}</span>
    </div>
    <p className="text-xl font-bold text-slate-100">{value?.toLocaleString() ?? '—'}</p>
  </div>
);

// ── CVE detail modal ──────────────────────────────────────────────────────
// Mirrors the searched-CVE detail card from VulnerabilityIntelligenceView.
// Receives the CVE row (which may be tile-only data, lazy-merged with the
// full vulnerabilities row once /api/vulnerabilities?search= resolves).
const CVEDetailModal = ({
  cve, loading, error, onClose,
  getSeverityStyle, getCvssStyle, formatRelativeTime
}) => {
  const inLocalDb = !!cve.id || !!cve.vuln_id; // truthy once full record is merged or vuln_id was on tile

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl my-8 bg-slate-900 border border-slate-700/60 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800/50 px-6 py-4 rounded-t-xl">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-2xl font-bold text-slate-100 mb-1 flex items-center gap-3 flex-wrap">
                <span className="font-mono">{cve.cve_id}</span>
                {cve.severity && (
                  <span className={`px-3 py-1 rounded-lg text-sm font-medium border ${getSeverityStyle(cve.severity)}`}>
                    {cve.severity}
                  </span>
                )}
                {cve.cvss_score && (
                  <span className={`text-lg font-bold ${getCvssStyle(cve.cvss_score)}`}>
                    CVSS {cve.cvss_score}
                  </span>
                )}
                {cve.cisa_kev && (
                  <span className="px-2 py-1 rounded text-xs font-medium border bg-red-500/20 border-red-500/40 text-red-300">
                    CISA KEV
                  </span>
                )}
                {loading && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3 animate-spin" /> loading
                  </span>
                )}
              </h3>
              {(cve.title || cve.vuln_title) && (
                <p className="text-slate-300 text-sm">{cve.title || cve.vuln_title}</p>
              )}
            </div>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Trending highlight */}
          {(cve.mention_count != null || cve.source_count != null) && (
            <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Flame className="w-5 h-5 text-orange-400" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-orange-300">
                    {cve.mention_count} mention{cve.mention_count === 1 ? '' : 's'} across {cve.source_count} source{cve.source_count === 1 ? '' : 's'}
                  </p>
                  {cve.first_seen && cve.last_seen && (
                    <p className="text-xs text-orange-200/70 mt-0.5">
                      First seen {formatRelativeTime(cve.first_seen)} · Last seen {formatRelativeTime(cve.last_seen)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-300">
              Couldn't load full CVE details: {error}
            </div>
          )}

          {!inLocalDb && !loading && (
            <div className="p-4 bg-slate-800/30 border border-slate-700/40 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-slate-200 font-medium">This CVE isn't in the local database yet.</p>
                <p className="text-slate-400 text-xs mt-1">
                  News mentions are shown below. Look up the full advisory on{' '}
                  <a
                    href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 underline"
                  >
                    NVD
                  </a>{' '}
                  or run an NVD sync to ingest it.
                </p>
              </div>
            </div>
          )}

          {/* Overview */}
          {(cve.published_date || cve.last_modified || cve.vendor || cve.cwe || cve.description) && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h4 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-cyan-400" />
                Overview
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                {cve.published_date && (
                  <div>
                    <span className="text-slate-500">Published:</span>
                    <span className="ml-2 text-slate-200">
                      {new Date(cve.published_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {cve.last_modified && (
                  <div>
                    <span className="text-slate-500">Last Modified:</span>
                    <span className="ml-2 text-slate-200">
                      {new Date(cve.last_modified).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {cve.vendor && (
                  <div>
                    <span className="text-slate-500">Vendor:</span>
                    <span className="ml-2 text-slate-200">{cve.vendor}</span>
                  </div>
                )}
                {cve.cwe && (
                  <div>
                    <span className="text-slate-500">CWE:</span>
                    <span className="ml-2 text-slate-200">{cve.cwe}</span>
                  </div>
                )}
              </div>
              {cve.description && (
                <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
                  <p className="text-sm text-slate-300 leading-relaxed">{cve.description}</p>
                </div>
              )}
            </div>
          )}

          {/* EPSS + Exploit */}
          {(cve.epss_score != null || cve.exploit_references) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cve.epss_score != null && (
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-cyan-400" />
                    <span className="font-semibold text-slate-200 text-sm">EPSS Score</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-100">
                    {(cve.epss_score * 100).toFixed(2)}%
                  </p>
                  {cve.epss_percentile != null && (
                    <p className="text-xs text-slate-400 mt-1">
                      {(cve.epss_percentile * 100).toFixed(1)}th percentile
                    </p>
                  )}
                </div>
              )}
              {cve.exploit_references && Array.isArray(cve.exploit_references) && cve.exploit_references.length > 0 && (
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Bug className="w-4 h-4 text-red-400" />
                    <span className="font-semibold text-slate-200 text-sm">Exploit References</span>
                  </div>
                  <p className="text-xs text-slate-400">
                    {cve.exploit_references.length} reference{cve.exploit_references.length === 1 ? '' : 's'}
                    {cve.exploit_source ? ` · ${cve.exploit_source}` : ''}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Affected Products */}
          {cve.affected_products && cve.affected_products.length > 0 && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h4 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-cyan-400" />
                Affected Products
              </h4>
              <div className="space-y-2">
                {cve.affected_products.map((product, idx) => (
                  <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <span className="text-sm text-slate-200 font-mono">{product}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CPEs */}
          {cve.cpes && cve.cpes.length > 0 && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h4 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-cyan-400" />
                CPE Identifiers
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {cve.cpes.map((cpe, idx) => (
                  <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                    <span className="text-xs text-slate-200 font-mono break-all">{cpe}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status indicators */}
          {(cve.exploit_available != null || cve.patch_available != null || cve.in_the_wild != null) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg border ${
                cve.exploit_available
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-slate-800/30 border-slate-700/50'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className={`w-5 h-5 ${cve.exploit_available ? 'text-red-400' : 'text-slate-500'}`} />
                  <span className="font-semibold text-slate-200 text-sm">Exploit Status</span>
                </div>
                <p className={`text-sm ${cve.exploit_available ? 'text-red-300' : 'text-slate-400'}`}>
                  {cve.exploit_available ? 'Public Exploit Available' : 'No Known Exploits'}
                </p>
              </div>

              <div className={`p-4 rounded-lg border ${
                cve.patch_available
                  ? 'bg-green-500/10 border-green-500/30'
                  : 'bg-slate-800/30 border-slate-700/50'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className={`w-5 h-5 ${cve.patch_available ? 'text-green-400' : 'text-slate-500'}`} />
                  <span className="font-semibold text-slate-200 text-sm">Patch Status</span>
                </div>
                <p className={`text-sm ${cve.patch_available ? 'text-green-300' : 'text-slate-400'}`}>
                  {cve.patch_available ? 'Patch Available' : 'No Patch Available'}
                </p>
              </div>

              <div className={`p-4 rounded-lg border ${
                cve.in_the_wild
                  ? 'bg-orange-500/10 border-orange-500/30'
                  : 'bg-slate-800/30 border-slate-700/50'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className={`w-5 h-5 ${cve.in_the_wild ? 'text-orange-400' : 'text-slate-500'}`} />
                  <span className="font-semibold text-slate-200 text-sm">In The Wild</span>
                </div>
                <p className={`text-sm ${cve.in_the_wild ? 'text-orange-300' : 'text-slate-400'}`}>
                  {cve.in_the_wild ? 'Actively Exploited' : 'Not Exploited in Wild'}
                </p>
              </div>
            </div>
          )}

          {/* GitHub repos */}
          {cve.github_top_repos && cve.github_top_repos.length > 0 && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h4 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Github className="w-5 h-5 text-cyan-400" />
                GitHub Repositories ({cve.github_repo_count})
              </h4>
              <div className="space-y-2">
                {cve.github_top_repos.map((repo, i) => (
                  <a
                    key={i}
                    href={repo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50 border border-slate-700/30 hover:border-cyan-500/30 hover:bg-slate-900 transition-all group/repo"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-mono text-cyan-400 group-hover/repo:text-cyan-300 truncate block">{repo.name}</span>
                      {repo.description && (
                        <span className="text-xs text-slate-500 line-clamp-1">{repo.description}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      {repo.language && (
                        <span className="text-[10px] text-slate-400 bg-slate-700/50 px-2 py-0.5 rounded">{repo.language}</span>
                      )}
                      <Star className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-xs text-slate-400">{repo.stars?.toLocaleString() || 0}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Top news articles */}
          {cve.top_articles && cve.top_articles.length > 0 && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h4 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-cyan-400" />
                In The News ({cve.top_articles.length})
              </h4>
              <div className="space-y-2">
                {cve.top_articles.map((art, i) => (
                  <a
                    key={i}
                    href={art.article_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 rounded-lg bg-slate-900/50 border border-slate-700/30 hover:border-cyan-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm text-slate-200 line-clamp-2 flex-1">{art.article_title}</p>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                      <span>{art.source_name}</span>
                      <span>{formatRelativeTime(art.published_at)}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Remediation */}
          {cve.workaround && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h4 className="text-lg font-semibold text-slate-100 mb-4">Remediation</h4>
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <p className="text-sm text-blue-200">{cve.workaround}</p>
              </div>
            </div>
          )}

          {/* References */}
          {cve.reference_urls && cve.reference_urls.length > 0 && (
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
              <h4 className="text-lg font-semibold text-slate-100 mb-4">References</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {cve.reference_urls.map((ref, idx) => (
                  <a
                    key={idx}
                    href={ref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 bg-slate-900/50 rounded-lg border border-slate-700/30 hover:border-cyan-500/30 transition-colors"
                  >
                    <span className="text-sm text-cyan-400 hover:text-cyan-300 break-all">{ref}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Footer link to NVD */}
          <div className="pt-2 flex items-center justify-end">
            <a
              href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors"
            >
              View on NVD <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TrendingFromNewsView;